// ---------------------------------------------------------------------------
// Print designs.
//
// Two families of hooks that never share a cache key, so a saved receipt does
// not invalidate the preparation ticket and the two screens cannot show each
// other's work.
//
// `usePrintConfig` is the one the TILL uses, and it is deliberately a separate
// query from the designer's list: a cashier has no `settings.manage`, so the
// list would 403 for them. Saving a design invalidates it, which is how a
// receipt printed a moment after the owner saves comes out with the new layout
// without anybody reloading the page.
// ---------------------------------------------------------------------------

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import type {
  Design,
  DesignListResponse,
  KitchenConfig,
  PrintConfigResponse,
  ReceiptConfig,
  ShiftReportConfig,
} from '../types/design';

type Kind = 'receipt' | 'kitchen' | 'shift-report';

const path = (kind: Kind) =>
  kind === 'receipt'
    ? '/receipt-designs'
    : kind === 'shift-report'
      ? '/shift-report-designs'
      : '/kitchen-designs';
const key = (kind: Kind) => ['designs', kind] as const;

export interface SaveDesignBody {
  id?: string;
  name: string;
  template: string;
  config: ReceiptConfig | KitchenConfig | ShiftReportConfig;
  activate?: boolean;
}

// ---------------------------------------------------------------------------
// The designers
// ---------------------------------------------------------------------------

export function useReceiptDesigns(enabled = true) {
  return useQuery({
    queryKey: key('receipt'),
    queryFn: () => api.get<DesignListResponse<ReceiptConfig>>(path('receipt')),
    enabled,
    staleTime: 30_000,
  });
}

export function useShiftReportDesigns(enabled = true) {
  return useQuery({
    queryKey: key('shift-report'),
    queryFn: () => api.get<DesignListResponse<ShiftReportConfig>>(path('shift-report')),
    enabled,
    staleTime: 30_000,
  });
}

export function useKitchenDesigns(enabled = true) {
  return useQuery({
    queryKey: key('kitchen'),
    queryFn: () => api.get<DesignListResponse<KitchenConfig>>(path('kitchen')),
    enabled,
    staleTime: 30_000,
  });
}

function useDesignMutation<TVariables>(
  kind: Kind,
  run: (variables: TVariables) => Promise<unknown>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: run,
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key(kind) });
      // What the till prints with may have just changed.
      void queryClient.invalidateQueries({ queryKey: ['print-config'] });
    },
  });
}

export function useSaveDesign(kind: Kind) {
  return useDesignMutation<SaveDesignBody>(kind, (body) =>
    api.post<{ design: Design<ReceiptConfig | KitchenConfig>; message: string }>(
      path(kind),
      body,
    ),
  );
}

export function useActivateDesign(kind: Kind) {
  return useDesignMutation<string>(kind, (id) =>
    api.post<{ message: string }>(`${path(kind)}/${id}/activate`, {}),
  );
}

export function useDuplicateDesign(kind: Kind) {
  return useDesignMutation<string>(kind, (id) =>
    api.post<{ message: string }>(`${path(kind)}/${id}/duplicate`, {}),
  );
}

// ---------------------------------------------------------------------------
// The till
// ---------------------------------------------------------------------------

/// The layouts to print with. Available to anybody signed in, because a
/// cashier prints every receipt.
export function usePrintConfig() {
  return useQuery({
    queryKey: ['print-config'],
    queryFn: () => api.get<PrintConfigResponse>('/print-config'),
    // Read on every sale. Long enough not to refetch per receipt, short enough
    // that an owner who changes the design sees it on the next till within the
    // minute even without the invalidation above.
    staleTime: 60_000,
  });
}

/// The layout ONE PARTICULAR TICKET was printed with. Same rule as the
/// receipt: a reprint has to match the paper the butcher was handed.
export function useKitchenConfigForVersion(versionId: string | null | undefined) {
  return useQuery({
    queryKey: ['print-config', 'kitchen', versionId],
    queryFn: () => api.get<{ kitchen: KitchenConfig }>(`/print-config/kitchen/${versionId}`),
    enabled: Boolean(versionId),
    staleTime: Infinity,
    retry: false,
  });
}

/// The layout ONE PARTICULAR SALE was printed with, for a reprint.
///
/// Returns undefined while loading and throws on 404, which the caller shows
/// as "this sale's original layout is no longer on file" rather than silently
/// reprinting with today's.
export function useReceiptConfigForVersion(versionId: string | null | undefined) {
  return useQuery({
    queryKey: ['print-config', 'receipt', versionId],
    queryFn: () => api.get<{ receipt: ReceiptConfig }>(`/print-config/receipt/${versionId}`),
    enabled: Boolean(versionId),
    // A stored version never changes - that is the entire point of it - so
    // this can be cached for as long as the tab is open.
    staleTime: Infinity,
    retry: false,
  });
}
